/**
 * Level.js
 * Retreives and contains the level information
 */

import { DesignDataTypes, Game } from "pixi_core";
import {
    BubbleColor,
    LEVEL_EDITOR_BUBBLE_GRID_OFFSET,
    LEVEL_EDITOR_BUBBLE_X_OFFSET,
    LEVEL_EDITOR_BUBBLE_y_OFFSET,
    LevelData,
    NB_MAX_ROW,
    NB_MAX_COLUMN_INITIAL_SETUP,
    BUBBLE_COLOR_GRAPHIC_TO_ID,
    BUBBLE_COLOR_NAME_TO_ID,
    BUBBLE_COLOR_ID_TO_GRAPHIC,
    FIRST_LEVEL
} from "../Constants";

export class Level {
    /**
     * @public
     */
    constructor() {
        /** @private @type {import("../Constants").GenerationConstraints} */
        this._generationConstraints = null;

        /** @private @type {Object} */
        this._initialBubbles = [];

        /** @private @type {Object[]} */
        this._respawnBubbles = [];

        /** @private @type {Object} */
        this._waves = [];

        /** @private @type {Number} */
        this._levelNumber = FIRST_LEVEL;
    }

    /**
     * Load the level json data
     * @public
     * @param {Number} level
     */
    load(level) {
        // Game levels 1-20
        let levelName = "Level" + level.toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: true });
        let levelNumber = Game.utils.assertNumber(level, FIRST_LEVEL);

        // Debug levels 21-26
        if (levelNumber === 21) {
            levelName = "Debug1";
        } else if (levelNumber === 22) {
            levelName = "Debug2";
        } else if (levelNumber === 23) {
            levelName = "Debug3";
        } else if (levelNumber === 24) {
            levelName = "Debug4";
        } else if (levelNumber === 25) {
            levelName = "Debug5";
        } else if (levelNumber >= 26) {
            levelName = "Debug6";
        }

        this._levelNumber = levelNumber;

        const levelData = JSON.parse(JSON.stringify(Game.design.getData(levelName, DesignDataTypes.eLEVEL)));

        let constraints = {};
        let bubbles = {};
        let waves = {};
        let respawnList = [];
        levelData.children.forEach((data) => {
            if (data.name === LevelData.eSETTINGS) constraints = data;
            if (data.name === LevelData.eWAVES) waves = data;
            if (data.name === LevelData.eBUBBLES) bubbles = data;
        });

        this._parseConstraints(constraints);
        this._parseWaves(waves);
        this._parseBubbles(bubbles, false);

        respawnList = this._generationConstraints.Respawn;

        for (let i = 0; i < respawnList.length; ++i) {
            let respawnPool = JSON.parse(JSON.stringify(Game.design.getData(respawnList[i], DesignDataTypes.eLEVEL)));
            if (respawnPool) {
                for (let j = 0; j < respawnPool.children.length; ++j) {
                    if (respawnPool.children[j].name === "Bubbles") {
                        this._parseBubbles(respawnPool.children[j], true);
                    }
                }
            }
        }
    }

    /**
     * @public
     */
    unload() {
        this._generationConstraints = null;
        this._initialBubbles = [];
        this._respawnBubbles = [];
        this._waves = [];
    }

    /**
     * Parse the constraints
     * @private
     * @param {Object} constraints
     */
    _parseConstraints(constraints) {
        // Remove level editor entity properties not useful in the game
        delete constraints.id;
        delete constraints.type;
        delete constraints.x;
        delete constraints.y;
        delete constraints.children;
        delete constraints.allowChildren;
        delete constraints.locked;
        delete constraints.name;

        this._generationConstraints = constraints;
    }

    /**
     * Parse the waves
     * @private
     * @param {Object} waveData
     */
    _parseWaves(waveData) {
        for (const wave of waveData.children) {
            this._waves.push([wave.Enemy1, wave.Enemy2, wave.Enemy3]);
        }
    }

    /**
     * Parse the bubbles
     * @private
     * @param {Object} bubbleData
     * @param {Boolean} isRespawn
     */
    _parseBubbles(bubbleData, isRespawn) {
        // Init empty array
        let respawnBubbles = [];
        for (let column = 0; column < NB_MAX_COLUMN_INITIAL_SETUP; ++column) {
            if (isRespawn) {
                respawnBubbles.push([]);
            } else {
                this._initialBubbles.push([]);
            }
            const maxRow = column % 2 === 0 ? NB_MAX_ROW : NB_MAX_ROW - 1;
            for (let row = 0; row < maxRow; ++row) {
                if (isRespawn) {
                    respawnBubbles[column].push(BubbleColor.eEMPTY);
                } else {
                    this._initialBubbles[column].push(BubbleColor.eEMPTY);
                }
            }
        }

        // Fill with level bubble
        for (const bubble of bubbleData.children) {
            const gridPos = this._bubblePosToGrid(bubble.x, bubble.y);
            // Shielded bubble
            if (bubble.graphic === BUBBLE_COLOR_ID_TO_GRAPHIC[BubbleColor.eSHIELDED]) {
                if (isRespawn) {
                    respawnBubbles[gridPos.column][gridPos.row] = {
                        special: this._convertColorToEnum(bubble.graphic),
                        color: this._convertNameToEnum(bubble.InsideColor)
                    };
                } else {
                    this._initialBubbles[gridPos.column][gridPos.row] = {
                        special: this._convertColorToEnum(bubble.graphic),
                        color: this._convertNameToEnum(bubble.InsideColor)
                    };
                }

                // Completely random bubble
            } else if (
                bubble.graphic === BUBBLE_COLOR_ID_TO_GRAPHIC[BubbleColor.eRANDOM] &&
                bubble.type === BUBBLE_COLOR_ID_TO_GRAPHIC[BubbleColor.eRANDOM]
            ) {
                if (isRespawn) {
                    respawnBubbles[gridPos.column][gridPos.row] = {
                        color: this._convertColorToEnum(bubble.graphic),
                        special: BUBBLE_COLOR_NAME_TO_ID[bubble.ClusterOfRandom]
                    };
                } else {
                    this._initialBubbles[gridPos.column][gridPos.row] = {
                        color: this._convertColorToEnum(bubble.graphic),
                        special: BUBBLE_COLOR_NAME_TO_ID[bubble.ClusterOfRandom]
                    };
                }

                // Chameleon bubble
            } else if (bubble.type === BUBBLE_COLOR_ID_TO_GRAPHIC[BubbleColor.eCHAMELEON]) {
                if (isRespawn) {
                    respawnBubbles[gridPos.column][gridPos.row] = {
                        color: BubbleColor.eRANDOM_EDITOR,
                        special: BubbleColor.eCHAMELEON
                    };
                } else {
                    this._initialBubbles[gridPos.column][gridPos.row] = {
                        color: BubbleColor.eRANDOM_EDITOR,
                        special: BubbleColor.eCHAMELEON
                    };
                }

                // Normal case
            } else {
                if (isRespawn) {
                    respawnBubbles[gridPos.column][gridPos.row] = {
                        color: this._convertColorToEnum(bubble.graphic),
                        special: this._convertColorToEnum(bubble.type)
                    };
                } else {
                    this._initialBubbles[gridPos.column][gridPos.row] = {
                        color: this._convertColorToEnum(bubble.graphic),
                        special: this._convertColorToEnum(bubble.type)
                    };
                }
            }
        }

        if (isRespawn) {
            this._respawnBubbles.push(respawnBubbles);
        }
    }

    /**
     * Convert bubble color to enum
     * @private
     * @param {String} colorString
     * @return {Number}
     */
    _convertColorToEnum(colorString) {
        /** @type {BubbleColor} */
        let colorNb = BubbleColor.eUNDEFINED;

        const id = BUBBLE_COLOR_GRAPHIC_TO_ID[colorString];
        if (Game.utils.isValidNumber(id) && id >= 0) {
            colorNb = id;
        }

        if (colorNb === BubbleColor.eRANDOM) colorNb = BubbleColor.eRANDOM_EDITOR;

        return colorNb;
    }

    /**
     * Convert bubble name to enum
     * @private
     * @param {String} nameString
     * @return {Number}
     */
    _convertNameToEnum(nameString) {
        /** @type {BubbleColor} */
        let colorNb = BubbleColor.eUNDEFINED;

        const id = BUBBLE_COLOR_NAME_TO_ID[nameString];
        if (Game.utils.isValidNumber(id) && id >= 0) {
            colorNb = id;
        }

        if (colorNb === BubbleColor.eRANDOM) colorNb = BubbleColor.eRANDOM_EDITOR;

        return colorNb;
    }

    /**
     * Converts a bubble postition to a grid position
     * @private
     * @param {Number} positionX
     * @param {Number} positionY
     * @returns {{column: Number, row: Number}}
     */
    _bubblePosToGrid(positionX, positionY) {
        const column = Math.abs(positionX / LEVEL_EDITOR_BUBBLE_X_OFFSET);
        const row =
            column % 2 === 0
                ? positionY / LEVEL_EDITOR_BUBBLE_y_OFFSET
                : (positionY - LEVEL_EDITOR_BUBBLE_GRID_OFFSET) / LEVEL_EDITOR_BUBBLE_y_OFFSET;

        return {
            column: Math.round(column),
            row: Math.round(row)
        };
    }

    /**
     * @public
     * @returns {Object}
     */
    getEnemies() {
        return this._waves;
    }

    /**
     * @public
     * @returns {Object}
     */
    getInitialBubbles() {
        return this._initialBubbles;
    }

    /**
     * @public
     * @param {Number} id
     * @returns {Object}
     */
    getRespawnBubbles(id) {
        return this._respawnBubbles[id];
    }

    /**
     * @public
     * @returns {Number}
     */
    getNbRespawnGrids() {
        return this._respawnBubbles.length;
    }

    /**
     * @public
     * @returns {String}
     */
    getFirstShooterBubble() {
        return this._generationConstraints.FirstBubble;
    }

    /**
     * @public
     * @returns {Number}
     */
    getAct() {
        return this._generationConstraints.Act;
    }

    /**
     * @public
     * @returns {Number}
     */
    getLevelNumber() {
        return this._levelNumber;
    }

    /**
     * @public
     * @returns {import("../Constants").GenerationConstraints}
     */
    getGenerationConstraints() {
        return this._generationConstraints;
    }

    /**
     * @public
     */
    destroy() {
        this.unload();
    }
}
